import { AxiosApi, ApiResult } from './axiosApi';
import { MaintenanceModeDto, MaintenanceModeStatusDto } from '../dtos/site';

export class MaintenanceModeApi extends AxiosApi<MaintenanceModeDto> {
    constructor(baseUri: string, logout?: () => void, enqueueSnackbar?: any) {
        super(baseUri, 'sites', logout, enqueueSnackbar);
    }

    // Several independent components (SiteInfo, SiteMaintenanceModeBanner,
    // SitesInMaintenanceModeWidget) each need the current site's status on load, but they
    // reach it via different, unsynchronized data-loading chains (some wait on their own
    // getById round trip first), so their getStatus calls land at different times rather than
    // concurrently. A short-lived cache collapses those into one request; writes invalidate it
    // so a change is never masked by a stale cached read.
    private static readonly STATUS_CACHE_TTL_MS = 5000;
    private statusCache = new Map<number, { promise: Promise<ApiResult<MaintenanceModeStatusDto>>; expiresAt: number }>();

    public getStatus = (siteId: number, options?: { forceRefresh?: boolean }) => {
        const cached = this.statusCache.get(siteId);
        if (!options?.forceRefresh && cached && cached.expiresAt > Date.now()) {
            return cached.promise;
        }

        const request = this.genericRequest({
            method: 'GET',
            extraPath: `${siteId}/maintenance-mode`,
        }) as Promise<ApiResult<MaintenanceModeStatusDto>>;

        this.statusCache.set(siteId, { promise: request, expiresAt: Date.now() + MaintenanceModeApi.STATUS_CACHE_TTL_MS });
        request.catch(() => this.statusCache.delete(siteId));

        return request;
    };

    public enable = (siteId: number, dto: MaintenanceModeDto) =>
        this.genericRequest({
            method: 'POST',
            extraPath: `${siteId}/maintenance-mode`,
            data: dto,
        }).then((r) => {
            this.statusCache.delete(siteId);
            return r;
        }) as Promise<ApiResult<MaintenanceModeDto>>;

    public extend = (siteId: number, newEndTime: string) =>
        this.genericRequest({
            method: 'PUT',
            extraPath: `${siteId}/maintenance-mode/extend`,
            data: { newEndTime },
        }).then((r) => {
            this.statusCache.delete(siteId);
            return r;
        }) as Promise<ApiResult<void>>;

    public disable = (siteId: number) =>
        this.genericRequest({
            method: 'DELETE',
            extraPath: `${siteId}/maintenance-mode`,
        }).then((r) => {
            this.statusCache.delete(siteId);
            return r;
        }) as Promise<ApiResult<void>>;

    public getActiveForDealer = (dealerId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `dealers/${dealerId}/maintenance-modes/active`,
        }) as Promise<ApiResult<MaintenanceModeDto[]>>;

    public getActiveForOrg = (orgId: number) =>
        this.genericRequest({
            method: 'GET',
            overridePath: `organizations/${orgId}/maintenance-modes/active`,
        }) as Promise<ApiResult<MaintenanceModeDto[]>>;

    public getActiveAll = () =>
        this.genericRequest({
            method: 'GET',
            overridePath: `maintenance-modes/active`,
        }) as Promise<ApiResult<MaintenanceModeDto[]>>;
}
